interface DosageTimelineProps {
  cycles: number;
  vialNumber: number;
  maintenanceDose: number;
  weeks: string;
  weight: string;
  loadingDose: number;
}

export const DosageWithoutSoliris = ({
  cycles,
  vialNumber,
  maintenanceDose,
  loadingDose,
  weeks,
  weight,
}: DosageTimelineProps) => {
  return (
    <div>
      <h2 className="mb-4 text-2xl font-bold">
        Patients with no prior eculizumab treatment
      </h2>

      <div className="w-full rounded-xl border border-gray-200 bg-white">
        {/* Header */}
        <div className="flex w-full items-center justify-between rounded-t-xl bg-[#E3F7FF] px-6 py-3">
          <div className="text-sm font-semibold text-gray-800">
            Weight Range {weight}
          </div>
        </div>

        <div className="w-full overflow-x-auto px-4 pb-4 pt-6">
          <svg
            width="628"
            height="120"
            viewBox="0 0 628 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g id="Group 10">
              {/* Maintenance Dose Circle and Label */}
              <g id="Frame 88">
                <circle id="Ellipse 3" cx="400" cy="51" r="7" fill="#008578" />
                <g id="Frame 66">
                  <text
                    id="2,100"
                    fill="black"
                    xmlSpace="preserve"
                    style={{ whiteSpace: "pre" }}
                    fontFamily="Roboto"
                    fontSize="14"
                    fontWeight="bold"
                    letterSpacing="0em"
                  >
                    <tspan x="382.199" y="82.7852">
                      {maintenanceDose.toLocaleString()} ml
                    </tspan>
                  </text>
                  <text
                    id="Vial"
                    fill="black"
                    xmlSpace="preserve"
                    style={{ whiteSpace: "pre" }}
                    fontFamily="Roboto"
                    fontSize="12"
                    letterSpacing="0em"
                  >
                    <tspan x="387.344" y="101.102">
                      {vialNumber} VIAL{vialNumber > 1 ? "S" : ""}
                    </tspan>
                  </text>
                </g>
              </g>

              {/* Cycles Info Box */}
              <g id="Frame 89">
                <path d="M627.5 37.5V64.5H441.5V37.5H627.5Z" fill="white" />
                <path d="M627.5 37.5V64.5H441.5V37.5H627.5Z" stroke="#008578" />
                <text
                  id="The same dose for next 6 cycles"
                  fill="#008578"
                  xmlSpace="preserve"
                  style={{ whiteSpace: "pre" }}
                  fontFamily="Roboto"
                  fontSize="13"
                  fontStyle="italic"
                  fontWeight="500"
                  letterSpacing="0em"
                >
                  <tspan x="451.033" y="54.9434">
                    The same dose for next {cycles} cycles
                  </tspan>
                </text>
              </g>

              {/* Timeline Line */}
              <path
                id="Line 3"
                d="M18 49C183.192 49.781 275.808 50.219 441 51"
                stroke="url(#paint0_linear_4003_101)"
                strokeWidth="4"
              />

              {/* Loading Dose Label */}
              <g id="Frame 91">
                <text
                  id="2,100_2"
                  fill="black"
                  xmlSpace="preserve"
                  style={{ whiteSpace: "pre" }}
                  fontFamily="Roboto"
                  fontSize="14"
                  fontWeight="bold"
                  letterSpacing="0em"
                >
                  <tspan x="201.199" y="81.7852">
                    {maintenanceDose.toLocaleString()} ml
                  </tspan>
                </text>
                <text
                  id="Vial_2"
                  fill="black"
                  xmlSpace="preserve"
                  style={{ whiteSpace: "pre" }}
                  fontFamily="Roboto"
                  fontSize="12"
                  letterSpacing="0em"
                >
                  <tspan x="206.344" y="100.102">
                    {vialNumber} VIAL{vialNumber > 1 ? "S" : ""}
                  </tspan>
                </text>
              </g>

              {/* Marker Rectangles */}
              <rect
                id="Rectangle 7"
                x="18"
                y="40"
                width="4"
                height="20"
                rx="2"
                fill="#008578"
              />
              <rect
                id="Rectangle 8"
                x="213"
                y="40"
                width="4"
                height="20"
                rx="2"
                fill="#008578"
              />

              {/* Loading Dose Circle + Dynamic Number */}
              <g id="loadingDose">
                <text
                  x="16"
                  y="99"
                  fill="white"
                  fontFamily="Roboto"
                  fontSize="10"
                  fontWeight="bold"
                >
                  {loadingDose}
                </text>
                <text
                  fill="black"
                  fontFamily="Roboto"
                  fontSize="14"
                  fontWeight="bold"
                >
                  <tspan x="16" y="82">
                    {loadingDose.toLocaleString()} ml
                  </tspan>
                </text>
                <text fill="black" fontFamily="Roboto" fontSize="12">
                  <tspan x="16" y="100">
                    {loadingDose / 300} VIAL{loadingDose / 300 > 1 ? "S" : ""}
                  </tspan>
                </text>
              </g>

              {/* Loading Dose Text */}
              <text
                id="Loading Dose"
                fill="#008578"
                xmlSpace="preserve"
                style={{ whiteSpace: "pre" }}
                fontFamily="Roboto"
                fontSize="12"
                fontWeight="bold"
                letterSpacing="0em"
              >
                <tspan x="0.396484" y="11.1016">
                  Loading
                </tspan>
                <tspan x="8.38867" y="25.1016">
                  Dose
                </tspan>
              </text>

              {/* Maintenance Dose Text */}
              <text
                id="Maintenance Dose"
                fill="#008578"
                xmlSpace="preserve"
                style={{ whiteSpace: "pre" }}
                fontFamily="Roboto"
                fontSize="12"
                fontWeight="bold"
                letterSpacing="0em"
              >
                <tspan x="180.002" y="11.1016">
                  Maintenance
                </tspan>
                <tspan x="201.389" y="25.1016">
                  Dose
                </tspan>
              </text>

              {/* Week Frames */}
              <g id="Frame 93">
                <path
                  d="M63 49.5C63 43.1487 68.1487 38 74.5 38H159.5C165.851 38 171 43.1487 171 49.5V51.5C171 57.8513 165.851 63 159.5 63H74.5C68.1487 63 63 57.8513 63 51.5V49.5Z"
                  fill="#A0DAB3"
                />
                <text
                  id="First 2 Weeks"
                  fill="#008578"
                  xmlSpace="preserve"
                  style={{ whiteSpace: "pre" }}
                  fontFamily="Roboto"
                  fontSize="13"
                  fontStyle="italic"
                  fontWeight="500"
                  letterSpacing="0em"
                >
                  <tspan x="83.1035" y="53.9434">
                    First 2 Weeks
                  </tspan>
                </text>
              </g>

              <g id="Frame 94">
                <path
                  d="M254 50.5C254 44.1487 259.149 39 265.5 39H350.5C356.851 39 362 44.1487 362 50.5V52.5C362 58.8513 356.851 64 350.5 64H265.5C259.149 64 254 58.8513 254 52.5V50.5Z"
                  fill="#A0DAB3"
                />
                <text
                  id="2 Weeks"
                  fill="#008578"
                  xmlSpace="preserve"
                  style={{ whiteSpace: "pre" }}
                  fontFamily="Roboto"
                  fontSize="13"
                  fontStyle="italic"
                  fontWeight="500"
                  letterSpacing="0em"
                >
                  <tspan x="287.413" y="55.9434">
                    {weeks} Weeks
                  </tspan>
                </text>
              </g>
            </g>

            <defs>
              <linearGradient
                id="paint0_linear_4003_101"
                x1="18.0005"
                y1="53.7978"
                x2="423.901"
                y2="53.7978"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#13756B" />
                <stop offset="1" stopColor="#178F84" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
    </div>
  );
};
